import React, {useEffect, useMemo, useRef, useState} from 'react';
import {useSelector} from 'react-redux';
import {Box, Typography} from '@mui/material';
import {t} from 'i18next';
import {ArrowDropUp} from '@mui/icons-material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';

import {RootState} from '../../redux/store';
import ArrTooltipWrapper from '../common/arr-tooltip-wrapper/ArrTooltipWrapper';
import ArrSingleSelectDropdown from '../common/arr-select-dropdown/ArrSingleSelectDropdown';
import {ISelectItem} from '../../shared/types';
import {
  useAddRedactionTag,
  useDeleteRedactionTag,
  useEditRedactionTag,
  useGetRedactionTagsList,
  useGetRedactionViewData,
} from '../../hooks/redactions/useRedaction';
import {ReactComponent as InfoIcon} from '../../assets/images/info-icon.svg';
import ArrButton from '../common/arr-button/ArrButton';
import ArrMenu, {
  ArrMenuHandle,
  CustomMenuItem,
} from '../common/arr-menu/ArrMenu';
import {ACTION_MENU, buildTree, findNodeById} from './redactionTagsUtils';
import './ViewRedactionTags.scss';
import {TreeViewBaseItem} from '@mui/x-tree-view';
import {ExtendedTreeItemProps} from '../../shared/interfaces';
import ArrLoadingSpinner from '../common/arr-loading-spinner/ArrLoadingSpinner';
import ArrTreeView from '../common/arr-tree-view/ArrTreeView';
import ArrModal from '../common/arr-modal/ArrModal';
import ArrTextField from '../common/arr-text-field/ArrTextField';
import {ToastType} from '../../shared/constants';
import ArrToastNotification from '../common/arr-toast-notification/ArrToastNotification';

const initialSelectedSecurityGroup: ISelectItem = {
  securityGroupID: '0',
  securityGroupName: t('tagsAndFolders.allSecurityGroups'),
};

const ALLOWED_NAME_REGEX = /^[A-Za-z0-9_ ]+$/;
const MAX_NAME_LENGTH = 255;

const validateRedactionTagName = (name: string): string => {
  const trimmed = name.trim();
  if (trimmed.length === 0) {
    return 'Name is required.';
  }
  if (name.length > MAX_NAME_LENGTH) {
    return `Maximum length is ${MAX_NAME_LENGTH} characters.`;
  }
  if (!ALLOWED_NAME_REGEX.test(name)) {
    return 'Special characters are not allowed.';
  }
  return '';
};

const ViewRedactionTags = () => {
  const loginUserData = useSelector(
    (state: RootState) => state.authenticatedUser.user
  );

  const {data: viewData, isLoading: isLoadingViewData} =
    useGetRedactionViewData();

  const actionMenuRef = useRef<ArrMenuHandle>(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<string>('');
  const [selectedSecurityGroup, setSelectedSecurityGroup] =
    useState<ISelectItem | null>(
      loginUserData.roleId === 1 ? initialSelectedSecurityGroup : null
    );
  const [redactionTagOperation, setRedactionTagOperation] = useState<
    'new' | 'edit' | 'delete' | null
  >(null);
  const [redactionTagName, setRedactionTagName] = useState<string>('');
  const [showModal, setShowModal] = useState<boolean>(false);
  const [redactionTagNameError, setRedactionTagNameError] =
    useState<string>('');
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastStatus, setToastStatus] = useState<ToastType>(ToastType.Success);
  const [originalRedactionTagName, setOriginalRedactionTagName] =
    useState<string>('');

  const queryParams = useMemo(
    () =>
      `?securityGroupId=${loginUserData.roleId === 1 ? selectedSecurityGroup?.securityGroupID : loginUserData.securityGroupId}`,
    [selectedSecurityGroup?.securityGroupID]
  );

  const {
    data: redactionTagsList,
    isLoading: isLoadingTagsList,
    refetch: refetchGetRedactionTagsList,
  } = useGetRedactionTagsList(queryParams, !!selectedSecurityGroup);

  const {mutate: mutateRedactionAddTag} = useAddRedactionTag();
  const {mutate: mutateRedactionEditTag} = useEditRedactionTag();
  const {mutate: mutateRedactionDeleteTag} = useDeleteRedactionTag();

  const getTreeDataList =
    useMemo((): TreeViewBaseItem<ExtendedTreeItemProps>[] => {
      return buildTree(redactionTagsList || []);
    }, [redactionTagsList]);

  const securityGroupList = useMemo(() => {
    if (loginUserData.roleId == 2) {
      const securityGroupData = viewData?.securityGroupList.find(
        (item) =>
          String(item.securityGroupID) === String(loginUserData.securityGroupId)
      );
      setSelectedSecurityGroup({
        securityGroupID: securityGroupData?.securityGroupID,
        securityGroupName: securityGroupData?.securityGroupName,
      });
    }
    const list =
      loginUserData.roleId === 1 ? viewData?.securityGroupList || [] : [];
    return loginUserData.roleId === 1
      ? [initialSelectedSecurityGroup, ...list]
      : [initialSelectedSecurityGroup];
  }, [viewData, loginUserData.roleId]);

  useEffect(() => {
    setSelectedItem('');
  }, [selectedSecurityGroup]);

  const handleActionMenuItemClick = (e: CustomMenuItem) => {
    console.log(e);
    setRedactionTagOperation(null);
    setShowModal(true);
    if (e.id === 'new') {
      setRedactionTagOperation(e.id);
    } else if (e.id === 'edit') {
      const editTag = redactionTagsList?.find(
        (e) => String(e.id) === String(selectedItem)
      );
      const currentName = editTag?.text ?? '';
      setRedactionTagName(currentName);
      setOriginalRedactionTagName(currentName);
      setRedactionTagOperation(e.id);
    } else if (e.id === 'delete') {
      const editTag = redactionTagsList?.find(
        (e) => String(e.id) === String(selectedItem)
      );
      const currentName = editTag?.text ?? '';
      setRedactionTagName(currentName);
      setRedactionTagOperation(e.id);
    }
  };

  const actionMenuItems = useMemo(() => {
    const menuOptions = ACTION_MENU;

    const CREATE_IDS = ['new'];

    const isCreateId = (id: string) => CREATE_IDS.includes(id);

    const mapMenu = (ruleFn: (id: string) => boolean) =>
      menuOptions.map((item) => {
        return {...item, disabled: !ruleFn(item.id)};
      });

    const treeData = getTreeDataList ?? [];
    const node = findNodeById(treeData, selectedItem);

    if (!node) return menuOptions;

    const isRoot = node.id === '-1';
    const isGroup = node.type === 'tags';
    const hasChildren = !!node.children?.length;

    if (isRoot) {
      return mapMenu((id) => isCreateId(id));
    }

    if (isGroup) {
      return mapMenu((id) => {
        if (isCreateId(id)) return true;
        if (id === 'edit') return true;
        if (id === 'delete' && !hasChildren) return true;
        return false;
      });
    }

    return mapMenu((id) => !isCreateId(id));
  }, [selectedItem]);

  const handleSelected = (value: string[] | null) => {
    if (Array.isArray(value) && value.length > 0) {
      setSelectedItem(value[0]);
    } else {
      setSelectedItem('');
    }
  };

  const triggerToast = (message: string, status: ToastType) => {
    setToastMessage(message);
    setToastStatus(status);
    setShowToast(true);
  };

  const isSaveDisabled = useMemo(() => {
    const trimmed = redactionTagName.trim();
    if (trimmed.length === 0) return true;
    if (redactionTagNameError) return true;
    if (redactionTagOperation === 'edit') {
      return trimmed === originalRedactionTagName.trim();
    }
    return false;
  }, [redactionTagName, redactionTagOperation, redactionTagNameError]);

  const handleOnSaveEvent = () => {
    const error = validateRedactionTagName(redactionTagName);
    if (error) {
      setRedactionTagNameError(error);
      return;
    }
    if (redactionTagOperation === 'delete') {
      const tagId = Number(selectedItem);

      mutateRedactionDeleteTag(tagId, {
        onSuccess: () => {
          triggerToast(
            t('redactionTags.action.deleteMessage'),
            ToastType.Success
          );
          setShowModal(false);
          setRedactionTagName('');
          setRedactionTagNameError('');
          refetchGetRedactionTagsList();
          setSelectedItem('');
        },
        onError: (error) => {
          triggerToast(error.message, ToastType.Error);
        },
      });
      return;
    }
    const payload = {
      parentID: Number(selectedItem),
      redactionName: redactionTagName,
      securityGroupID: Number(selectedSecurityGroup?.securityGroupID || 0),
      isLocked: true,
      documentID: 0,
      nodeID: '',
      isSelected: false,
    };
    if (redactionTagOperation === 'new') {
      mutateRedactionAddTag(payload, {
        onSuccess: () => {
          triggerToast(t('redactionTags.action.addMessage'), ToastType.Success);
          setShowModal(false);
          setRedactionTagName('');
          setRedactionTagNameError('');
          refetchGetRedactionTagsList();
        },
        onError: (error) => {
          triggerToast(error.message, ToastType.Error);
        },
      });
    } else if (redactionTagOperation === 'edit') {
      mutateRedactionEditTag(
        {payload, tagId: Number(selectedItem)},
        {
          onSuccess: () => {
            triggerToast(
              t('redactionTags.action.editMessage'),
              ToastType.Success
            );
            setShowModal(false);
            setRedactionTagName('');
            setRedactionTagNameError('');
            refetchGetRedactionTagsList();
          },
          onError: (error) => {
            triggerToast(error.message, ToastType.Error);
          },
        }
      );
    }
  };

  return (
    <Box className="view-redaction-tags-main">
      <Box className="view-redaction-tags-header">
        <Typography
          className="typo-text headline3_size redaction-tags-title-text"
          data-testid={`view-redaction-tags-title`}>
          {t('redactionTags.title')}
        </Typography>
        <ArrTooltipWrapper
          title={t('redactionTags.toolTip.toolTipTitle')}
          trigger={
            <InfoIcon fontSize="medium" className="redaction-tags-info-icon" />
          }
        >
          {t('redactionTags.toolTip.toolTipDescription')}
        </ArrTooltipWrapper>
      </Box>
      <Box className="action-container">
        <Box className="filters-section">
          <Box
            className={
              loginUserData.roleId !== 1
                ? 'security-group-dropdown-disabled'
                : ''
            }
          >
            <ArrSingleSelectDropdown
              selectedValues={selectedSecurityGroup}
              dropdownLabel=""
              dropdownItems={securityGroupList as ISelectItem[]}
              dropdownItemKey="securityGroupID"
              dropdownItemDisplay="securityGroupName"
              customWidth={17}
              sizeVariant="medium"
              onSelectionChange={setSelectedSecurityGroup}
              disabled={loginUserData.roleId !== 1}
              searchable
              showNoResultsText
              dataTestId="asd-security-group-id"
            />
          </Box>
        </Box>
        <Box>
          <ArrButton
            variant="primary"
            size="small"
            dataTestId="action-button"
            endIcon={isMenuOpen ? <ArrowDropUp /> : <ArrowDropDownIcon />}
            aria-label={t('assignment.dataGrid.action')}
            className={`action-dropdown-button action-dropdown-button--selected`}
            onClick={(e) => {
              actionMenuRef.current?.open(e.currentTarget);
              setIsMenuOpen(true);
            }}
            id="action-button"
            name="action-button"
          >
            {t('assignment.dataGrid.action')}
          </ArrButton>
          <ArrMenu
            ref={actionMenuRef}
            items={actionMenuItems}
            direction="bottom-left"
            subMenuDirection="left"
            onItemClick={(e) => handleActionMenuItemClick(e)}
            className="action-submenu"
            onClose={() => {
              setIsMenuOpen(false);
            }}
            id="action-submenu"
          />
        </Box>
      </Box>
      {isLoadingTagsList || isLoadingViewData ? (
        <Box className="loading-container">
          <ArrLoadingSpinner />
        </Box>
      ) : (
        <Box className="tree-container">
          <ArrTreeView
            setSelectedItem={handleSelected}
            itemsList={getTreeDataList}
            selectedItem={selectedItem}
            handleNoResultMessage={t('common.noResultFound')}
            allowGlobalSelect={false}
            multipleSelection={false}
            isShowCheckbox={false}
            showActions={false}
            isShowCountLabel={true}
            showTooltip
            defaultExpandedIds={['-1']}
            expandOnSearch={true}
          />
        </Box>
      )}
      <ArrModal
        showModal={showModal}
        onClose={() => {
          setShowModal(false);
          setRedactionTagName('');
          setRedactionTagNameError('');
        }}
        className="redaction-tag-modal"
        dataTestId="arr-modal-tag"
        title={redactionTagOperation === 'delete' ? t('common.delete') : ''}
        description={
          redactionTagOperation === 'delete'
            ? t('redactionTags.action.deleteTitle')
            : ''
        }
        heading={redactionTagOperation === 'delete' ? redactionTagName : ''}
        buttons={[
          {
            id: '1',
            label:
              redactionTagOperation === 'delete'
                ? t('common.no')
                : t('common.cancel'),
            onClick: () => {
              setShowModal(false);
              setRedactionTagName('');
              setRedactionTagNameError('');
            },
            variant: 'secondary',
            dataTestId: 'redaction-tag-modal-cancel-btn',
          },
          {
            id: '1',
            label:
              redactionTagOperation === 'delete'
                ? t('common.yes')
                : t('common.save'),
            onClick: handleOnSaveEvent,
            variant: 'primary',
            dataTestId: 'tag-modal-save-btn',
            disabled: isSaveDisabled,
          },
        ]}
      >
        {redactionTagOperation !== 'delete' && (
          <Box className="modal-container">
            <Typography
              className="typo-text headline3_size redaction-tag-title"
              data-testid={`view-redaction-tags-typography`}>
              {redactionTagOperation === 'new'
                ? t('redactionTags.action.new')
                : t('redactionTags.action.edit')}
            </Typography>
            <Box className="text-field-area">
              <ArrTextField
                className="name-field"
                value={redactionTagName}
                onChange={(e) => {
                  setRedactionTagName(e.target.value);
                  if (redactionTagNameError) setRedactionTagNameError('');
                }}
                showErrorIcon={Boolean(redactionTagNameError)}
                state={redactionTagNameError ? 'error' : 'enabled'}
                label="Name*"
                dataTestId="redaction-tag-name-field"
                supportText={redactionTagNameError}
                slotProps={{htmlInput: {maxLength: 255}}}
              />
            </Box>
          </Box>
        )}
      </ArrModal>
      {showToast && (
        <ArrToastNotification
          data-testid="tag-toast"
          message={toastMessage}
          onClose={() => {
            setToastMessage('');
            setShowToast(false);
          }}
          status={[toastStatus]}
          setErrorIconAsAlert
        />
      )}
    </Box>
  );
};

export default ViewRedactionTags;
